import { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Breadcrumb from '@/components/base/Breadcrumb';
import MovieCard from '@/components/base/MovieCard';
import EmptyState from '@/components/base/EmptyState';
import { movies } from '@/mocks/movies';
import { genres, genreNameMap } from '@/mocks/genres';
import { usePageMeta } from '@/hooks/usePageMeta';

const PAGE_SIZE = 8;

export default function CategoryPage() {
  const { type } = useParams<{ type: string }>();
  const [visible, setVisible] = useState(PAGE_SIZE);

  const genreName = type ? genreNameMap[type] : undefined;

  const list = useMemo(() => {
    if (!type) return [];
    return movies
      .filter((movie) => movie.genres.includes(type))
      .sort((a, b) => b.heat - a.heat);
  }, [type]);

  usePageMeta({
    title: genreName ? `${genreName}电影 - 草民电影` : '电影类型 - 草民电影',
    description: genreName
      ? `草民电影${genreName}类型片单，精选该类型下热门与高分影片，包含海报、片名、评分与剧情简介。`
      : '草民电影按类型浏览影片。',
  });

  useEffect(() => {
    setVisible(PAGE_SIZE);
  }, [type]);

  const shown = list.slice(0, visible);

  if (!genreName) {
    return (
      <div className="w-full px-4 py-16 md:px-6">
        <EmptyState
          title="未找到该电影类型"
          message="你访问的类型不存在，看看其他类型吧。"
          icon="ri-movie-2-line"
          actionLabel="返回首页"
          onAction={() => window.REACT_APP_NAVIGATE('/')}
        />
      </div>
    );
  }

  return (
    <div className="w-full px-4 py-8 md:px-6 md:py-10">
      <Breadcrumb
        items={[
          { label: '首页', to: '/' },
          { label: '电影类型' },
          { label: `${genreName}电影` },
        ]}
      />

      <header className="mt-5">
        <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
          {genreName}电影
        </h1>
        <p className="mt-2 max-w-3xl text-sm leading-relaxed text-foreground-500">
          共收录 {list.length} 部{genreName}类型影片，按热度排序，持续更新中。
        </p>
      </header>

      <div className="mt-6 flex flex-wrap gap-2">
        {genres.map((genre) => (
          <Link
            key={genre.key}
            to={`/movies/category/${genre.key}`}
            className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
              genre.key === type
                ? 'bg-primary-500 text-background-50'
                : 'bg-secondary-100 text-secondary-900 hover:bg-secondary-200'
            }`}
          >
            {genre.name}
          </Link>
        ))}
      </div>

      <div className="mt-8">
        {shown.length === 0 ? (
          <EmptyState
            title="该类型暂无影片"
            message="换个类型看看吧，或者返回首页浏览热门推荐。"
            icon="ri-film-line"
            actionLabel="返回首页"
            onAction={() => window.REACT_APP_NAVIGATE('/')}
          />
        ) : (
          <>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
              {shown.map((movie) => (
                <MovieCard key={movie.id} movie={movie} />
              ))}
            </div>
            {visible < list.length && (
              <div className="mt-8 flex justify-center">
                <button
                  type="button"
                  onClick={() => setVisible((prev) => prev + PAGE_SIZE)}
                  className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-6 py-2.5 text-sm font-semibold text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
                >
                  <i className="ri-add-line" /> 加载更多
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}